import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  FaChartLine,
  FaCoins,
  FaStar,
  FaCrown,
  FaUsers,
  FaBolt,
  FaWallet,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import { useAppContext } from "../context/AppContext";

const fmt2 = (n) => Number(n || 0).toFixed(2);

const PLAN_LABELS = {
  plan1: "Core Growth",
  plan2: "Institutional Blend",
  plan3: "Founder's Syndicate",
};

/* ── Theme ── */
const T = {
  bg: "#050d1a",
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  blue: "#3b82f6",
  blueDim: "rgba(59,130,246,0.1)",
  purple: "#8b5cf6",
  purpleDim: "rgba(139,92,246,0.1)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
  successDim: "rgba(34,197,94,0.1)",
};

/* color map for revenue parts */
const PART_COLORS = {
  roi: { solid: T.accent, dim: T.accentDim, glow: "rgba(234,179,8,0.4)" },
  ref: { solid: T.blue, dim: T.blueDim, glow: "rgba(59,130,246,0.4)" },
  team: { solid: T.purple, dim: T.purpleDim, glow: "rgba(139,92,246,0.4)" },
  other: { solid: "#64748b", dim: "rgba(100,116,139,0.12)", glow: "rgba(100,116,139,0.3)" },
};

export default function Analytics() {
  const { FetchUserData, HandleFetchUserData } = useAppContext();

  useEffect(() => {
    HandleFetchUserData?.();
  }, [HandleFetchUserData]);

  const stats = useMemo(() => {
    const u = FetchUserData || {};
    const earnings = u.earnings || {};

    const activePlanArr = Array.isArray(u.activePlan) ? u.activePlan : [];
    const last = activePlanArr.length ? activePlanArr[activePlanArr.length - 1] : null;
    const plan = last?.plan || null;

    const rawPlanName = String(plan?.name || "").toLowerCase().trim();
    const planName = PLAN_LABELS[rawPlanName] || plan?.name || "Inactive";

    const todayDone = Number(u?.todayTasksCompleted ?? 0);
    const totalTasks = 2;
    const progressPct = Math.max(0, Math.min(100, (todayDone / totalTasks) * 100));

    const principal = Number(plan?.amount || 0);
    const dailyROIPct = Number(plan?.dailyROIPct || 0);
    const todayRoiEstimate = principal > 0 ? (principal * dailyROIPct) / 100 : 0;

    const totalEarnings = Number(u.totalEarnings || 0);

    const roi = Number(earnings.roi || 0);
    const referral = Number(earnings.referralCommission || 0);
    const team = Number(earnings.teamProfitShare || 0);
    const other = Number(earnings.other || 0);

    const parts = [
      { key: "roi", label: "ROI Yield", value: roi },
      { key: "ref", label: "Referral", value: referral },
      { key: "team", label: "Team Share", value: team },
      { key: "other", label: "Other", value: other },
    ];

    const sumParts = parts.reduce((s, x) => s + x.value, 0) || 1;
    const credits = Array.isArray(plan?.credits) ? plan.credits : [];
    const recentCredits = credits.slice(-5).reverse();

    return {
      name: u.name || "Partner",
      level: Number(u.level || 0),
      points: Number(u.TotalPoints || 0),
      totalEarnings,
      roi,
      referral,
      team,
      other,
      planActive: !!u.isActivePlan,
      planName,
      principal,
      dailyROIPct,
      todayRoiEstimate,
      planProfit: Number(plan?.totalProfit || 0),
      todayDone,
      totalTasks,
      progressPct,
      parts,
      sumParts,
      recentCredits,
    };
  }, [FetchUserData]);

  return (
    <div style={{
      width: "100%", minHeight: "100vh",
      color: T.textPrimary, fontFamily: "system-ui, -apple-system, sans-serif",
      paddingBottom: 80,
    }}>
      <div style={{
        maxWidth: 1280, margin: "0 auto",
        padding: "20px clamp(12px, 3vw, 24px) 0",
        display: "flex", flexDirection: "column", gap: 28,
      }}>

        {/* ══════════ HEADER ══════════ */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: "flex", flexDirection: "column", gap: 6 }}
        >
          <h1 style={{
            fontSize: "clamp(1.8rem, 5vw, 2.4rem)",
            fontWeight: 900, color: T.textPrimary,
            textTransform: "uppercase", letterSpacing: "-0.03em", margin: 0,
          }}>
            Performance <span style={{ color: T.accent }}>Node</span>
          </h1>
          <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.3em", margin: 0 }}>
            Institutional Grade Analytics
          </p>
        </motion.div>

        {/* ══════════ TOP KPIs ══════════ */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          gap: 18,
        }}>
          <KpiCard title="Gross Earnings" value={`$${fmt2(stats.totalEarnings)}`} icon={FaCoins} color={T.accent} delay={0} />
          <KpiCard title="ROI Yield" value={`$${fmt2(stats.roi)}`} icon={FaChartLine} color={T.blue} delay={0.08} />
          <KpiCard title="Network Comm." value={`$${fmt2(stats.referral)}`} icon={FaUsers} color={T.purple} delay={0.16} />
          <KpiCard title="Asset Points" value={`${stats.points}`} icon={FaStar} color={T.success} delay={0.24} />
        </div>

        {/* ══════════ MAIN GRID ══════════ */}
        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
        }}>

          {/* ── Revenue Mix Card — from left ── */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 32, 
              padding: "clamp(20px, 4vw, 30px) clamp(20px, 4vw, 28px)",
              position: "relative", overflow: "hidden",
              boxShadow: "0 16px 50px rgba(0,0,0,0.4)",
              flex: "2 1 min(100%, 500px)",
            }}
          >
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg,${T.accent},${T.blue},transparent)`,
            }} />
            <div style={{
              position: "absolute", top: -40, right: -40,
              width: 160, height: 160, borderRadius: "50%",
              background: "radial-gradient(ellipse,rgba(234,179,8,0.06) 0%,transparent 70%)",
              pointerEvents: "none",
            }} />

            <div style={{
              display: "flex", flexWrap: "wrap", justifyContent: "space-between",
              alignItems: "flex-start", gap: 16, marginBottom: 28, position: "relative", zIndex: 1,
            }}>
              <div>
                <h2 style={{ fontSize: "clamp(1.1rem, 3vw, 1.2rem)", fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", margin: "0 0 4px" }}>
                  Revenue Mix
                </h2>
                <p style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", margin: 0 }}>
                  Portfolio Distribution
                </p>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontSize: 9, fontWeight: 900, color: T.accent, textTransform: "uppercase", letterSpacing: "0.18em", margin: "0 0 4px" }}>
                  Total Value
                </p>
                <p style={{ fontSize: "clamp(1.4rem, 4vw, 1.6rem)", fontWeight: 900, color: T.textPrimary, margin: 0 }}>
                  ${fmt2(stats.totalEarnings)}
                </p>
              </div>
            </div>

            {/* distribution bar */}
            <div style={{
              width: "100%", height: 14,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 999, overflow: "hidden",
              display: "flex", marginBottom: 26, position: "relative", zIndex: 1,
            }}>
              {stats.parts.map((p, i) => (
                <motion.div
                  key={p.key}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(p.value / stats.sumParts) * 100}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.1, ease: "easeOut" }}
                  style={{ height: "100%", background: PART_COLORS[p.key].solid }}
                />
              ))}
            </div>

            {/* legend grid */}
            <div style={{
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
              gap: 12, position: "relative", zIndex: 1,
            }}>
              {stats.parts.map((p, i) => (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.07, duration: 0.5 }}
                  style={{
                    background: "rgba(255,255,255,0.02)",
                    border: `1px solid ${T.border}`,
                    borderRadius: 16, padding: "13px 16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: PART_COLORS[p.key].solid, boxShadow: `0 0 8px ${PART_COLORS[p.key].glow}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 900, color: T.textSecondary, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
                      {p.label}
                    </span>
                  </div>
                  <span style={{ fontWeight: 900, color: T.textPrimary, fontSize: 13, marginLeft: 10 }}>
                    ${fmt2(p.value)}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* meta stats */}
            <div style={{
              marginTop: 26, display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: 12,
              position: "relative", zIndex: 1,
            }}>
              <MiniStat label="Rank" value={`LVL ${stats.level}`} icon={FaCrown} color={T.purple} />
              <MiniStat label="Est. Daily" value={`$${fmt2(stats.todayRoiEstimate)}`} icon={FaBolt} color={T.accent} />
              <MiniStat label="Wallet Ref" value="SECURE" icon={FaWallet} color={T.success} />
            </div>
          </motion.div>

          {/* ── Asset Plan Card — from right ── */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.border}`,
              borderRadius: 32, 
              padding: "clamp(20px, 4vw, 30px) clamp(20px, 4vw, 28px)",
              position: "relative", overflow: "hidden",
              boxShadow: "0 16px 50px rgba(0,0,0,0.4)",
              display: "flex", flexDirection: "column", gap: 22,
              flex: "1 1 min(100%, 300px)",
            }}
          >
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: stats.planActive
                ? `linear-gradient(90deg,${T.success},transparent)`
                : `linear-gradient(90deg,${T.textMuted},transparent)`,
            }} />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 1, flexWrap: "wrap", gap: 10 }}>
              <h2 style={{ fontSize: "1.1rem", fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", margin: 0 }}>
                Asset Plan
              </h2>
              <span style={{
                padding: "5px 14px", borderRadius: 999,
                fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.15em",
                background: stats.planActive ? T.successDim : "rgba(255,255,255,0.04)",
                color: stats.planActive ? T.success : T.textMuted,
                border: `1px solid ${stats.planActive ? "rgba(34,197,94,0.3)" : T.border}`,
              }}>
                {stats.planActive ? "Active" : "Inactive"}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative", zIndex: 1 }}>
              <Row label="Tier" value={stats.planName} />
              <Row label="Capital" value={`$${fmt2(stats.principal)}`} />
              <Row label="Daily %" value={`${fmt2(stats.dailyROIPct)}%`} />
              <Row label="Total Profit" value={`$${fmt2(stats.planProfit)}`} />
            </div>

            <div style={{
              background: T.accentDim,
              border: `1px solid rgba(234,179,8,0.25)`,
              borderRadius: 18, padding: 16,
              display: "flex", gap: 12, position: "relative", zIndex: 1,
            }}>
              <FaInfoCircle style={{ color: T.accent, flexShrink: 0, marginTop: 2 }} size={14} />
              <p style={{ fontSize: 10, color: "#fde68a", fontWeight: 700, lineHeight: 1.6, textTransform: "uppercase", margin: 0 }}>
                Active tasks are mandatory to maintain ROI credit eligibility for current cycle.
              </p>
            </div>

            <div style={{ position: "relative", zIndex: 1 }}>
              <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", marginBottom: 12 }}>
                Recent Credits
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {stats.recentCredits.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.06 }}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      background: "rgba(255,255,255,0.02)",
                      border: `1px solid ${T.border}`,
                      padding: "11px 16px", borderRadius: 14,
                    }}
                  >
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase" }}>
                      {c.date}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 900, color: T.success }}>
                      +${fmt2(c.amount)}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* ══════════ TASK PROGRESS ══════════ */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: 32, 
            padding: "clamp(20px, 4vw, 30px) clamp(20px, 4vw, 28px)",
            position: "relative", overflow: "hidden",
            boxShadow: "0 16px 50px rgba(0,0,0,0.4)",
          }}
        >
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: `linear-gradient(90deg,${T.blue},${T.accent},transparent)`,
          }} />

          <div style={{
            display: "flex", flexWrap: "wrap", justifyContent: "space-between",
            alignItems: "center", gap: 16, marginBottom: 24, position: "relative", zIndex: 1,
          }}>
            <div>
              <h2 style={{ fontSize: "clamp(1.1rem, 3vw, 1.2rem)", fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", margin: "0 0 4px" }}>
                Task Efficiency
              </h2>
              <p style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.18em", margin: 0 }}>
                Daily Operations
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{ fontSize: "clamp(1.5rem, 4vw, 1.7rem)", fontWeight: 900, color: T.blue }}>
                {Math.round(stats.progressPct)}%
              </span>
              <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", margin: 0 }}>
                {stats.todayDone} of {stats.totalTasks} Done
              </p>
            </div>
          </div>

          <div style={{
            width: "100%", height: 10,
            background: "rgba(255,255,255,0.05)",
            borderRadius: 999, marginBottom: 28, overflow: "hidden",
            position: "relative", zIndex: 1,
          }}>
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${stats.progressPct}%` }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: "easeOut" }}
              style={{
                height: "100%", borderRadius: 999,
                background: `linear-gradient(90deg,${T.blue},${T.accent})`,
                boxShadow: `0 0 14px rgba(59,130,246,0.5)`,
              }}
            />
          </div>

          <div style={{
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
            gap: 14, position: "relative", zIndex: 1,
          }}>
            <StatusChip label="Operations" value={stats.todayDone >= 2 ? "Completed" : "In-Progress"} ok={stats.todayDone >= 2} />
            <StatusChip label="Remaining" value={` ${Math.max(0, stats.totalTasks - stats.todayDone)} Nodes` } ok={stats.todayDone >= 2}/>
            <StatusChip label="Eligibility" value={stats.todayDone >= 2 ? "Qualified" : "Pending"} ok={stats.todayDone >= 2} />
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* ───────────── UI Sub-Components ───────────── */

function KpiCard({ title, value, icon: Icon, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.94 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
      whileHover={{ y: -6 }}
      style={{
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: 26, padding: "clamp(20px, 4vw, 26px)",
        position: "relative", overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
        transition: "border-color 0.25s, box-shadow 0.25s",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = `${color}55`;
        e.currentTarget.style.boxShadow = `0 16px 40px rgba(0,0,0,0.4), 0 0 28px ${color}22`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = T.border;
        e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.35)";
      }}
    >
      <div style={{
        position: "absolute", top: -20, right: -20,
        width: 90, height: 90, borderRadius: "50%",
        background: `radial-gradient(ellipse,${color}1a 0%,transparent 70%)`,
        pointerEvents: "none",
      }} />
      <div style={{
        width: 46, height: 46, borderRadius: 16,
        background: `${color}18`,
        border: `1px solid ${color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        marginBottom: 20, color,
        position: "relative", zIndex: 1,
      }}>
        <Icon size={18} />
      </div>
      <p style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em", margin: "0 0 6px", position: "relative", zIndex: 1 }}>
        {title}
      </p>
      <div style={{ fontSize: "clamp(1.3rem, 3vw, 1.5rem)", fontWeight: 900, color: T.textPrimary, position: "relative", zIndex: 1 }}>
        {value}
      </div>
    </motion.div>
  );
}

function MiniStat({ label, value, icon: Icon, color }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.02)",
      border: `1px solid ${T.border}`,
      borderRadius: 16, padding: 14,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 11,
        background: `${color}18`,
        border: `1px solid ${color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color, flexShrink: 0,
      }}>
        <Icon size={13} />
      </div>
      <div style={{ overflow: "hidden" }}>
        <p style={{ fontSize: 8, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </p>
        <p style={{ fontSize: 11, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value}
        </p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", flexWrap: "wrap", gap: 8 }}>
      <span style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em" }}>
        {label}
      </span>
      <span style={{ fontSize: 12, fontWeight: 900, color: T.textPrimary, textTransform: "uppercase", textAlign: "right" }}>
        {value}
      </span>
    </div>
  );
}

function StatusChip({ label, value, ok = false }) {
  return (
    <div style={{
      border: `1px solid ${ok ? "rgba(34,197,94,0.3)" : T.border}`,
      background: ok ? T.successDim : "rgba(255,255,255,0.02)",
      borderRadius: 16, padding: "13px 16px",
      display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
    }}>
      <span style={{ fontSize: 9, fontWeight: 900, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.15em" }}>
        {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 900, color: ok ? T.success : T.textPrimary, textTransform: "uppercase", textAlign: "right" }}>
        {value}
      </span>
    </div>
  );
}